import { ProductSelection } from "@components/foundation/ProductSelection";
import { TrustedByCompanies } from "@components/foundation/TrustedByCompanies";
import { FAQSection, Question } from "components/FAQSection";
import Layout from "components/foundation/Layout";
import { CTASection } from "components/foundation/CTASection";
import { ReactElement } from "react";
import {
  DISCORD_LINK,
  HUMANLOOP_API_DOCS_LINK,
  HUMANLOOP_DOCS_LINK,
  SLACK_LINK,
} from "utils/constants";

const Pricing: NextPageTypes.NextPageWithLayout = () => (
  <main className="mt-[120px]">
    <div className="container-lg grid-12 mx-auto  selection:border selection:bg-blue-200">
      <div className="col-span-full text-center">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-medium font-display text-[48px]  text-gray-900">
            Build differentiated AI products
          </h2>
          <p className="mt-8 text-18-24 text-gray-600">
            Whether you’re starting to build or you need to scale, we’ve got a
            plan for you.
          </p>
        </div>
      </div>
    </div>
    <div className="container-lg grid-12 mt-[120px]">
      <div className=" col-span-10 col-start-2 lg:col-span-8 lg:col-start-3 ">
        <ProductSelection />
      </div>
    </div>
    {/* <PricingHero /> */}
    <div className="container-lg grid-12 mx-auto my-[20px]">
      <div className="col-span-full sm:col-span-10  sm:col-start-2 lg:col-span-8 lg:col-start-3 ">
        <FAQSection questions={questions} fullWidth />
      </div>
    </div>
    <div className="my-[100px]" />
    <TrustedByCompanies />
    <CTASection className="my-[300px] bg-gray-100" />
  </main>
);

Pricing.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout xlWidth title="Humanloop – Pricing">
      {page}
    </Layout>
  );
};

export default Pricing;

const questions: Question[] = [
  {
    question: "What is Humanloop?",
    answer:
      "Humanloop is the development platform for building AI-powered features and applications. Prompt experimentation, cost optimization and fine-tuning incorporating user feedback to help you to build differentiated and scaleable AI products.",
    defaultOpen: true,
  },
  {
    question: "What counts as a datapoint?",
    answer:
      "A datapoint is a single completion by the machine learning model (such as GPT-4) which is stored in Humanloop and enriched with additional metadata like initial inputs, user feedback, correction through the UI or API.",
  },
  {
    question: "How does the free trial work?",
    answer:
      "When you sign up for your trial of Humanloop you'll have access to all features of your chosen plan. At any point during the trial you may choose to cancel your subscription without any charges from our side.",
  },
  {
    question: "How long does it take to setup a working system?",
    answer:
      "It can take minutes to create a new AI endpoint from the playground. To set up an end-to-end system where your AI features in production are using all Humanloop features around logging and feedback collection may take a few hours. We have guides to help you get started quickly.",
  },
  {
    question: "What is fine tuning?",
    answer:
      "Fine tuning is customising the model by further training on your own data. It can significantly improve performance.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Your data is encrypted, private to your account and never shared with third parties. Our platform is regularly penetration tested to ensure we meet the best security standards. Our enterprise plans offer additional dedicated cloud deployment options.",
  },
  {
    question: "Can I export my data?",
    answer: "Yes, you can export all your data at any time.",
  },
  {
    question: "Do I pay for OpenAI, Anthropic etc. separately?",
    answer:
      "Yes – you bring your own API key from the AI providers and pay for your usage with them separately. This means that you can use any custom terms you have with them and any fine tuned models created are accessible outside of Humanloop.",
  },

  {
    question: "What data formats are supported?",
    answer: (
      <>
        JSON and CSV formats are supported for uploading data via the user
        interface or the API. See{" "}
        <a href={HUMANLOOP_DOCS_LINK}>our documentation</a> for further details.
      </>
    ),
  },
  {
    question: "Can I use my own model or other providers?",
    answer: (
      <>
        Our APIs and SDK allow you to use our tools with your own model provider
        where relevant. See <a href={HUMANLOOP_DOCS_LINK}>our documentation</a>{" "}
        for more details.
      </>
    ),
  },
  {
    question: "How can I get support?",
    answer: (
      <>
        Join our <a href={DISCORD_LINK}>community Discord server</a> or send us
        an email at <a href="mailto:team@humanloop.com">team@humanloop.com</a>.
      </>
    ),
  },
];
